(function (global) {
  'use strict';

  function isObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function cloneValue(value, path, ancestors) {
    if (!Array.isArray(value) && !isObject(value)) {
      return { isValid: true, value: value, issue: null };
    }

    if (ancestors.has(value)) {
      return {
        isValid: false,
        value: null,
        issue: { code: 'cyclic-legacy-answer', path: path }
      };
    }

    ancestors.add(value);
    try {
      const copy = Array.isArray(value) ? [] : {};
      const keys = Object.keys(value);

      for (let index = 0; index < keys.length; index += 1) {
        const key = keys[index];
        const childPath = path + '.' + key;
        const child = cloneValue(value[key], childPath, ancestors);

        if (!child.isValid) {
          return child;
        }

        Object.defineProperty(copy, key, {
          configurable: true,
          enumerable: true,
          writable: true,
          value: child.value
        });
      }

      return { isValid: true, value: copy, issue: null };
    } catch (error) {
      return {
        isValid: false,
        value: null,
        issue: { code: 'malformed-legacy-answer', path: path }
      };
    } finally {
      ancestors.delete(value);
    }
  }

  function hasPopulatedValue(value) {
    return value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);
  }

  function createResult(status, answers, issues) {
    return {
      status: status,
      answers: answers,
      issues: issues
    };
  }

  function collectValidationIssues(normalizedOutput) {
    const issues = [];

    normalizedOutput.validationErrors.forEach(function (entry, entryIndex) {
      if (!isObject(entry) || !Array.isArray(entry.errors)) {
        issues.push({
          code: 'malformed-validation-error',
          path: 'normalizedOutput.validationErrors[' + entryIndex + ']'
        });
        return;
      }

      entry.errors.forEach(function (error, errorIndex) {
        issues.push({
          code: isObject(error) && typeof error.code === 'string' ? error.code : 'malformed-validation-error',
          path: 'normalizedOutput.validationErrors[' + entryIndex + '].errors[' + errorIndex + ']'
        });
      });
    });

    return issues;
  }

  function adaptNormalizedAnswersInternal(normalizedOutput, legacyRawAnswers) {
    if (!isObject(normalizedOutput)) {
      return createResult('invalid', {}, [{ code: 'malformed-normalized-output', path: 'normalizedOutput' }]);
    }

    if (!Array.isArray(normalizedOutput.answers)) {
      return createResult('invalid', {}, [{ code: 'malformed-normalized-answers', path: 'normalizedOutput.answers' }]);
    }

    if (!Array.isArray(normalizedOutput.validationErrors)) {
      return createResult('invalid', {}, [{ code: 'malformed-validation-errors', path: 'normalizedOutput.validationErrors' }]);
    }

    if (!isObject(legacyRawAnswers)) {
      return createResult('invalid', {}, [{ code: 'malformed-legacy-answers', path: 'legacyRawAnswers' }]);
    }

    const answers = {};
    const issues = [];
    const answerKeys = new Set();

    normalizedOutput.answers.forEach(function (answer, index) {
      const path = 'normalizedOutput.answers[' + index + ']';

      if (!isObject(answer) || typeof answer.answerKey !== 'string' || !answer.answerKey) {
        issues.push({ code: 'malformed-normalized-answer', path: path });
        return;
      }

      if (answerKeys.has(answer.answerKey)) {
        issues.push({ code: 'duplicate-answer-key', path: path + '.answerKey' });
        return;
      }
      answerKeys.add(answer.answerKey);

      if (Object.prototype.hasOwnProperty.call(legacyRawAnswers, answer.answerKey)) {
        const cloned = cloneValue(
          legacyRawAnswers[answer.answerKey],
          'legacyRawAnswers.' + answer.answerKey,
          new WeakSet()
        );

        if (cloned.isValid) {
          answers[answer.answerKey] = cloned.value;
        } else {
          issues.push(cloned.issue);
        }
      } else if (hasPopulatedValue(answer.value)) {
        issues.push({
          code: 'missing-legacy-answer',
          path: 'legacyRawAnswers.' + answer.answerKey
        });
      }
    });

    const hasMappingIssues = issues.length > 0;
    const validationIssues = collectValidationIssues(normalizedOutput);
    Array.prototype.push.apply(issues, validationIssues);

    const malformed = issues.some(function (issue) {
      return issue.code.indexOf('malformed-') === 0 || issue.code === 'duplicate-answer-key';
    });
    const nonRequiredValidation = validationIssues.some(function (issue) {
      return issue.code !== 'required';
    });
    const invalidEnvelopeStatus = normalizedOutput.status !== 'valid' && normalizedOutput.status !== 'invalid';

    if (hasMappingIssues || malformed || nonRequiredValidation || invalidEnvelopeStatus) {
      if (invalidEnvelopeStatus) {
        issues.push({ code: 'invalid-normalized-status', path: 'normalizedOutput.status' });
      }
      return createResult('invalid', answers, issues);
    }

    if (normalizedOutput.status === 'invalid' || validationIssues.length) {
      return createResult('partial', answers, issues);
    }

    return createResult('compatible', answers, issues);
  }

  /**
   * Adapts ordered Questionnaire Engine output to the existing legacy answer shape.
   * Values are copied from the original legacy snapshot so their representation is
   * preserved exactly. Hidden questions are absent from normalized output and are
   * therefore excluded from the returned answers. Malformed input never throws.
   *
   * @param {Object} normalizedOutput Questionnaire Engine normalized answer envelope.
   * @param {Object} legacyRawAnswers Original legacy answers keyed by answerKey.
   * @returns {{status: string, answers: Object, issues: Object[]}} Structured compatibility result.
   */
  function adaptNormalizedAnswers(normalizedOutput, legacyRawAnswers) {
    try {
      return adaptNormalizedAnswersInternal(normalizedOutput, legacyRawAnswers);
    } catch (error) {
      return createResult('invalid', {}, [{ code: 'adapter-error', path: 'adapter' }]);
    }
  }

  const api = {
    adaptNormalizedAnswers
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.QuestionnaireCompatibilityAdapter = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
